import { Typography } from 'components/generic/typography';
import styled from 'styled-components';
import type {
    ClickAndCollectBasketStoreViewModel,
    ClickAndCollectDeliveryOption,
    OnlineDeliveryOption
} from 'autogen/swagger/all';
import { useTranslation } from 'react-i18next';
import {
    ClickAndCollectNoStores,
    ClickAndCollectStoresHeading,
    SingleClickAndCollectStoreHeading
} from 'autogen/translation-keys/trans-website-basket-deliveryfix';
import StoreCard from 'components/StoreCard';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { removeListStyling } from 'modules/helpers/mixins';
import StatusItem from 'components/StatusItem';
import StatusRenderer from 'components/StoreCard/components/StatusRenderer';
import ProductImage from './ProductImage';
import TruncatedGrid from 'components/TruncatedGrid';
import { ShowLessProductsText } from 'autogen/translation-keys/trans-website-checkout-delivery';
import DefaultToggleButton from 'components/TruncatedGrid/components/DefaultToggleButton';
import HighlightedText from 'components/generic/HighlightedText';

export type ClickAndCollectStoresProps = {
    model?: ClickAndCollectDeliveryOption;
    selectedDeliveryMethodId?: string | number;
    setSelectedDeliveryMethodId: Dispatch<SetStateAction<string | number | undefined>>;
    setSelectedDeliveryMethod: Dispatch<
        SetStateAction<OnlineDeliveryOption | ClickAndCollectBasketStoreViewModel | undefined>
    >;
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing8};
`;

const List = styled.ul`
    ${removeListStyling}

    display: grid;
    gap: ${({ theme }) => theme.Spacing16};
    min-width: 0;
`;

const AdditionalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing12};
`;

function ClickAndCollectStores(props: ClickAndCollectStoresProps) {
    const { model, selectedDeliveryMethodId, setSelectedDeliveryMethodId, setSelectedDeliveryMethod } = props;
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedDeliveryMethod(model?.stores[0]);
        setSelectedDeliveryMethodId(model?.stores[0].model.parcelShopId);
    }, []);

    function additionalContent(store) {
        return (
            <AdditionalContent>
                <StatusItem statusRenderer={() => <StatusRenderer status={store.model.clickAndCollectStoreStatus} />}>
                    {store.model.basketStoreStockStatus}
                </StatusItem>
            </AdditionalContent>
        );
    }

    return (
        <Wrapper>
            {model && model?.stores.length > 0 ? (
                <>
                    <Typography.Body as="span" size="L" strong>
                        {t(model.stores.length > 1 ? ClickAndCollectStoresHeading : SingleClickAndCollectStoreHeading)}
                    </Typography.Body>
                    <List>
                        {model.stores.map((store, i) => (
                            <StoreCard
                                key={i}
                                model={store.model}
                                selected={store.model.parcelShopId === selectedDeliveryMethodId}
                                setSelectedId={setSelectedDeliveryMethodId}
                                nameType={'findInStore'}
                                showAdditionalInfoCollapsed={false}
                                additionalContentCollapsedRenderer={() => additionalContent(store)}
                                additionalContentRenderer={() => (
                                    <AdditionalContent>
                                        {additionalContent(store)}
                                        <TruncatedGrid
                                            cellWidth={56}
                                            cellHeight={72}
                                            visibleItems={5}
                                            ToggleButtonRenderer={(props) => (
                                                <DefaultToggleButton
                                                    collapseText={t(ShowLessProductsText)}
                                                    {...props}
                                                />
                                            )}
                                        >
                                            {store.model.productsWithStoreStockStatus.map((product, i) => (
                                                <ProductImage
                                                    key={i}
                                                    image={product.image.url}
                                                    unavailable={!product.isAvailableInStore}
                                                />
                                            ))}
                                        </TruncatedGrid>
                                    </AdditionalContent>
                                )}
                                onClick={() => setSelectedDeliveryMethod(store)}
                            />
                        ))}
                    </List>
                </>
            ) : (
                <HighlightedText color="ColorSystemNegative1" withBorder={false}>
                    {t(ClickAndCollectNoStores)}
                </HighlightedText>
            )}
        </Wrapper>
    );
}

export default ClickAndCollectStores;
